import OperationButton, {
  followType,
} from "@/components/FollowButton/operationButton";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import Image from "@/components/Image";
import { MuseTag } from "@/components/Muse";
import ShareDropdown from "@/components/ShareDropdown";
import { UserGuideTooltip } from "@/components/UserGuideTooltip";
import UserInfoCard from "@/components/UserInfoCard";
import useAppStore from "@/context/store";
import { updateBoard } from "@/services";
import reporter from "@/tracker/report/reporter";
import { getPage } from "@/tracker/shared/global";
import displayNum from "@/utils/displayNum";
import getTimeDuration from "@/utils/getTimeDuration";
import imageUrl from "@/utils/imageUrl";
import strEllipsis from "@/utils/strEllipsis";
import useLocalStorage from "@/utils/useLocalStorage";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { Button, Dropdown, Tooltip } from "antd";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import styles from "./index.module.less";

interface IProps {
  tab: string;
  board: IBoardInfo;
  setBoard: (board: IBoardInfo) => void;
  isNew: boolean;
  liked: boolean;
  following: boolean;
  goBatch: () => void;
  onBoardEdit: () => void;
  onToggleLike: () => void;
  onToggleFollow: () => void;
  onChangeTab: (tab: string) => void;
  onCategorySelect: (category: string) => void;
  enableDragSort?: boolean;
}

export function BoardPageHeader({
  isNew,
  board,
  setBoard,
  goBatch,
  onBoardEdit,
  liked,
  following,
  onToggleFollow,
  onToggleLike,
  onChangeTab,
  onCategorySelect,
  enableDragSort,
}: IProps) {
  const { user, config, listCacheMap } = useAppStore(
    ({ user, config, listCacheMap }) => ({
      user,
      config,
      listCacheMap,
    })
  );

  const report = useMemoizedFn((buttonName) => {
    // 埋点上报
    try {
      const pageName = getPage();
      const trackData = {
        event_id: 39008,
        event_name: "button_click",
        button_name: buttonName,
        board_id: boardId,
        ...pageName,
      };

      reporter(trackData, { bd_disabled: true });
    } catch (e) {
      // do nothing
    }
  });

  const open = useMemoizedFn((_, buttomName) => {
    report(buttomName);
  });

  const setFollowUser = (value) => {
    setBoard({ ...board, user_following: value });
  };

  const router = useRouter();
  const { boardId, tab } = router.query;

  // 奇葩的待归类画板
  const isSpecial = board?.is_private === 2;

  const pics = useMemo(() => {
    return board?.pins?.map((pin) => imageUrl(pin.file)) || [];
  }, [board]);

  const isOwner = useMemo(() => {
    if (!board?.user?.user_id || !user?.user_id) {
      return false;
    }
    return board?.user?.user_id === user?.user_id;
  }, [board, user]);

  // 上榜信息
  const listInfo = useMemo(() => {
    // 如果不在榜上，返回空数组
    if (!listCacheMap || !board?.board_id || !listCacheMap[board?.board_id]) {
      return [];
    } else {
      // 反转数组，因为最后面的是最新的，放在最前面
      return listCacheMap[board?.board_id].reverse();
    }
  }, [listCacheMap, board]);

  const isOfficial = useMemo(() => {
    return config?.officialUsers?.split(",").includes(board?.user?.urlname);
  }, [board, config]);

  return (
    <div className={classNames(styles.header, isNew && styles.new)}>
      <div className={styles.top}>
        <div className={styles.left}>
          <h1 className={styles.title}>{board?.title}</h1>
        </div>
        <div className={styles.right}>
          <If condition={isSpecial}>
            <Then>
              <ShareDropdown
                placement="bottomCenter"
                pics={pics}
                title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                  board?.title,
                  45
                )}相关的设计灵感，来看看：`}
              >
                <Button
                  className={classNames([styles.button, styles.rect])}
                  type="text"
                  data-gd-click="button_click"
                  data-button-name="分享"
                  data-board-id={board?.board_id}
                >
                  <HBIcon className={styles.icon} type="ic_share" />
                </Button>
              </ShareDropdown>
              <When condition={isOwner}>
                <Button
                  type="text"
                  className={styles.button}
                  data-gd-click="button_click"
                  data-button-name="批量管理"
                  data-board-id={board?.board_id}
                  onClick={goBatch}
                >
                  管理采集
                </Button>
              </When>
            </Then>
            <Else>
              <If condition={isOwner}>
                <Then>
                  <ShareDropdown
                    placement="bottomCenter"
                    pics={pics}
                    title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                      board?.title,
                      45
                    )}相关的设计灵感，来看看：`}
                  >
                    <Button
                      className={classNames([styles.button, styles.rect])}
                      type="text"
                      data-gd-click="button_click"
                      data-button-name="分享"
                      data-board-id={board?.board_id}
                    >
                      <HBIcon className={styles.icon} type="ic_share" />
                    </Button>
                  </ShareDropdown>
                  <UserGuideTooltip
                    needUserId={false}
                    placement="bottom"
                    localStorageKey={"HB_BOARD_EDIT_DRAGSORT"}
                    visible={enableDragSort}
                    title="现已支持拖拽更改采集排序"
                  >
                    <Button
                      type="text"
                      className={styles.button}
                      icon={
                        !isNew && (
                          <HBIcon className={styles.icon} type="batch1" />
                        )
                      }
                      data-gd-click="button_click"
                      data-button-name="批量管理"
                      data-board-id={board?.board_id}
                      onClick={goBatch}
                    >
                      管理采集
                    </Button>
                  </UserGuideTooltip>
                  <Button
                    className={styles.button}
                    icon={
                      !isNew && (
                        <HBIcon className={styles.icon} type="hk_create" />
                      )
                    }
                    type="text"
                    onClick={onBoardEdit}
                    data-gd-click="button_click"
                    data-button-name="编辑"
                    data-board-id={board?.board_id}
                  >
                    编辑信息
                  </Button>
                </Then>
                <Else>
                  <ShareDropdown
                    placement="bottomCenter"
                    pics={pics}
                    title={`这个来自「花瓣网」的画板不错哦，有很多${strEllipsis(
                      board?.title,
                      45
                    )}相关的设计灵感，来看看：`}
                  >
                    <Button
                      className={classNames([styles.button, styles.rect])}
                      type="text"
                      data-gd-click="button_click"
                      data-button-name="分享"
                      data-board-id={board?.board_id}
                    >
                      <HBIcon className={styles.icon} type="ic_share" />
                    </Button>
                  </ShareDropdown>
                  <Button
                    className={classNames([
                      styles.button,
                      styles.rect,
                      liked ? styles.active : null,
                    ])}
                    type="text"
                    title={liked ? "取消喜欢" : "喜欢"}
                    onClick={onToggleLike}
                    data-gd-click="button_click"
                    data-button-name={liked ? "取消喜欢" : "喜欢"}
                    data-board-id={board?.board_id}
                  >
                    <HBIcon
                      className={styles.icon}
                      type={liked ? "ic_favorite" : "favorite"}
                    />
                  </Button>
                  <If condition={following}>
                    <Then>
                      {board?.user_following === followType.unIndependent ? (
                        <Button
                          className={styles.button}
                          type="text"
                          onClick={onToggleFollow}
                          data-gd-click="button_click"
                          data-button-name="取消画板"
                          data-board-id={board?.board_id}
                        >
                          已关注
                        </Button>
                      ) : (
                        <OperationButton
                          type="text"
                          className={styles.operationButton}
                          onClick={onToggleFollow}
                          data-gd-click="button_click"
                          data-button-name="取消画板"
                          data-board-id={board?.board_id}
                          boardInfo={board}
                        ></OperationButton>
                      )}
                    </Then>
                    <Else>
                      <Button
                        className={styles.button}
                        type={isNew ? "text" : "primary"}
                        onClick={onToggleFollow}
                        data-gd-click="button_click"
                        data-button-name="关注画板"
                        data-board-id={board?.board_id}
                        icon={
                          !isNew && (
                            <HBIcon className={styles.icon} type="ic_star" />
                          )
                        }
                      >
                        关注画板
                      </Button>
                    </Else>
                  </If>
                </Else>
              </If>
            </Else>
          </If>
        </div>
      </div>

      <div className={styles.bottom}>
        <When condition={board?.description}>
          <pre className={styles.desc}>{board?.description}</pre>
        </When>
        {/* 入榜展示 */}
        <div className={styles.listContent}>
          {listInfo.map((item, index) => {
            return (
              <a
                className={`${styles.card} ${item.href ? styles.hasLink : ""}`}
                key={index}
                onClick={() => open(item.href, item.title)}
                href={item.href}
                target="_blank"
              >
                {item.icon?.length > 0 && (
                  <Image
                    className={styles.cardImg}
                    alt=""
                    src={item.icon[0].url}
                    width={20}
                  ></Image>
                )}

                <div>
                  入选<span className={styles.cardTitle}>{item.title}</span>
                </div>
                {item.href && (
                  <HBIcon type="ic_chevron_right" className={styles.cardIcon} />
                )}
              </a>
            );
          })}
        </div>
        <div className={styles.more}>
          {isOwner && isNew ? (
            <>
              <div
                className={classNames([
                  styles.tabItem,
                  tab === "pin" || !tab ? styles.active : null,
                ])}
                onClick={() => onChangeTab("pin")}
              >
                <div className={styles.title}>采集</div>
                {board?.pin_count > 0 && (
                  <div className={styles.content}>
                    {displayNum(board?.pin_count)}
                  </div>
                )}
              </div>
              <div
                className={classNames([
                  styles.tabItem,
                  tab === "follow" ? styles.active : null,
                ])}
                onClick={() => onChangeTab("follow")}
              >
                <div className={styles.title}>被关注</div>
                <div className={styles.content}>
                  {displayNum(board?.follow_count)}
                </div>
              </div>
            </>
          ) : (
            <When condition={!!board?.user}>
              <Dropdown
                placement="bottomLeft"
                disabled={isOwner}
                overlay={
                  <UserInfoCard
                    board={board}
                    setFollowUser={setFollowUser}
                    isOfficial={isOfficial}
                  ></UserInfoCard>
                }
              >
                <a
                  target="_blank"
                  href={`/user/${board?.user?.urlname}`}
                  className={styles.user}
                >
                  <div className={styles.avatar}>
                    <img
                      alt={board?.user?.username}
                      src={imageUrl(board?.user?.avatar, {
                        width: 75,
                        rect: "sq",
                      })}
                    />
                  </div>
                  <div className={styles.name}>{board?.user?.username}</div>
                  <When condition={isOfficial}>
                    <span className={styles.official} title="花瓣官方"></span>
                  </When>
                  <When condition={board?.user?.extra?.is_museuser}>
                    <MuseTag user={board?.user} />
                  </When>
                </a>
              </Dropdown>
            </When>
          )}
          <When condition={!isSpecial && (isOwner || board?.category_id)}>
            <div className={styles.category}>
              <span className={styles.categoryTitle}>所属分类：</span>
              <BoardCategorySelect
                board={board}
                isOwner={isOwner}
                onChange={onCategorySelect}
              />
            </div>
          </When>
          <When condition={board?.updated_at}>
            {board?.pin_count > 0 && (
              <div className={styles.time}>
                更新于 {getTimeDuration({ time: board?.updated_at * 1000 })}
              </div>
            )}
          </When>

          {!isOwner && board?.pin_count > 0 && isNew && (
            <div className={styles.time}>
              共{displayNum(board?.pin_count)}张采集
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BoardCategorySelect({ onChange, board, isOwner }) {
  const categories = useAppStore((store) => store.categories);
  const { runAsync } = useReq(updateBoard, {
    manual: true,
  });
  const [visible, setVisible] = useState(false);
  const [tipVisible, setTipVisible] = useLocalStorage(
    (userId) => `_PINIT_FRONT_USER_GUIDE_SIGN_${userId}_8_`,
    {
      loadingValue: false,
      defaultValue: true,
      needUserId: true,
    }
  );

  const onSelect = async (category) => {
    const result: any = await runAsync({
      board_id: board.board_id,
      category: category.value,
    });
    if (!result.err) {
      HBMessage.success({
        content: (
          <span
            data-dialog-title="画板分类修改成功"
            data-gd-expose="dialog_expose"
          >
            画板分类修改成功
          </span>
        ),
      });
      setVisible(false);
      onChange && onChange(category);
    }
  };

  const menu = (
    <div className={styles.menu}>
      <div className={styles.list}>
        {categories.map((category) => (
          <a
            key={category.id}
            className={styles.item}
            onClick={() => onSelect(category)}
            data-gd-click="button_click"
            data-button-name="修改分类"
            data-content-source={category.name}
          >
            {category.name}
          </a>
        ))}
      </div>
    </div>
  );

  const link = (
    <div className={styles.link}>
      <span className={styles.text}>{board?.category_name || "请选择"}</span>
      <HBIcon className={styles.icon} type="ic_arrow_down" />
    </div>
  );

  return (
    <If condition={isOwner}>
      <Then>
        <If condition={tipVisible}>
          <Then>
            <Dropdown
              overlay={menu}
              placement="bottomCenter"
              trigger={["hover"]}
              getPopupContainer={(triggerNode) => triggerNode}
              visible={visible}
              onVisibleChange={(visible) => {
                if (visible) {
                  setTipVisible(false);
                }
                setVisible(visible);
              }}
            >
              <Tooltip
                overlayClassName={styles.help}
                getPopupContainer={(triggerNode) => triggerNode}
                placement="bottomLeft"
                title={
                  <div className={styles.content}>
                    <span>选择画板分类，方便管理灵感</span>
                    <HBIcon
                      className={styles.close}
                      type="ic_clear2"
                      onClick={() => setTipVisible(false)}
                    />
                  </div>
                }
                visible={true}
              >
                {link}
              </Tooltip>
            </Dropdown>
          </Then>
          <Else>
            <Dropdown
              overlay={menu}
              placement="bottomCenter"
              trigger={["hover"]}
              visible={visible}
              onVisibleChange={(visible) => setVisible(visible)}
              getPopupContainer={(triggerNode) => triggerNode}
            >
              {link}
            </Dropdown>
          </Else>
        </If>
      </Then>
      <Else>
        <a target="_blank" href={`/discovery/${board?.category_id}`}>
          {board?.category_name}
        </a>
      </Else>
    </If>
  );
}
